/* Painel do Professor — escopo turma.

   Diferença em relação aos outros perfis: a turma NÃO está em user_roles (um
   professor tem várias), então ele escolhe. A escolha fica no contexto
   persistido do PerfilContext, e o seletor volta pelo botão "trocar turma".

   O AppShell do projeto de origem foi substituído pelo PainelShell (D7): sem
   sidebar e sem índice de seções; ficam o filtro de período e o breadcrumb. */

import React, { useMemo, useState } from "react";

import PropTypes from "prop-types";

import { TurmaPainelView } from "./TurmaPainel";
import { BuscaNome, SemResultadoBusca } from "../../components/BuscaNome";
import PainelShell from "../../components/PainelShell";
import { PeriodoFiltro } from "../../components/PeriodoFiltro";
import { usePerfil } from "../../context/PerfilContext";
import { api } from "../../lib/api";
import { filtrarPorNome } from "../../lib/busca";
import { periodoInicial } from "../../lib/periodo";
import { useAsync } from "../../lib/useAsync";
import { useContextoIE } from "../../lib/useContextoIE";

/* Seleção de turma do professor.

   ⚠️ Só Tailwind aqui, nenhuma classe do Bootstrap. Este componente vive DENTRO
   do `.ie-root`, e as duas bibliotecas têm utilitárias de MESMO NOME com valores
   diferentes — as de altura/largura 100 do Bootstrap significam 100%, enquanto
   no Tailwind as de mesmo nome valem 25rem (400px). Como o CSS do painel é
   escopado, o Tailwind vence: a primeira versão desta tela saiu com cards de
   400px de altura e larguras irregulares.

   Os nomes não aparecem escritos aqui de propósito — o Tailwind varre até os
   comentários e geraria a regra perigosa de volta.

   Regra: todo o painel é Tailwind. O CoreUI/Bootstrap fica na página que
   hospeda a rota (InteligenciaEducacional.jsx), fora do wrapper. */
function SeletorTurma({ onEscolher }) {
  const turmas = useAsync(() => api.professorTurmas({}), []);
  const [busca, setBusca] = useState("");

  // Busca em nome da turma E da escola: um professor com turmas em duas escolas
  // procura tanto por "9º B" quanto pelo nome da unidade.
  //
  // Passou a usar `filtrarPorNome` junto da extração do componente de busca —
  // antes era um `includes()` cru aqui, que não achava "antonio" em "Antônio".
  const filtradas = useMemo(
    () => filtrarPorNome(turmas.data, busca, ["turma", "escola"]),
    [turmas.data, busca]
  );

  if (turmas.loading) {
    return (
      <p className="text-[13px] text-[var(--color-ink-faint)]">
        Carregando suas turmas…
      </p>
    );
  }
  if (turmas.error) {
    return (
      <p className="text-[13px] text-[var(--color-bad)]">{turmas.error}</p>
    );
  }
  if (!turmas.data || turmas.data.length === 0) {
    return (
      <p className="text-[13px] text-[var(--color-ink-faint)]">
        Nenhuma turma encontrada para o seu usuário.
      </p>
    );
  }

  return (
    <div>
      <BuscaNome
        id="ie-busca-turma"
        value={busca}
        onChange={setBusca}
        placeholder="Buscar turma ou escola…"
        total={turmas.data.length}
        filtrado={filtradas.length}
        rotulo="turma"
      />

      {filtradas.length === 0 ? (
        <SemResultadoBusca termo={busca} item="turma" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtradas.map(t => (
            <button
              key={t.id}
              type="button"
              className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-colors hover:border-[var(--color-brand)] hover:bg-[var(--color-brand-dim)]"
              onClick={() =>
                onEscolher({
                  turmaId: t.id,
                  turmaNome: t.turma,
                  escolaId: t.escola_id,
                  escolaNome: t.escola
                })
              }
            >
              <span className="block text-[14px] font-semibold text-[var(--color-ink)]">
                {t.turma}
              </span>
              <span className="mt-0.5 block text-[12px] text-[var(--color-ink-soft)]">
                {t.escola}
              </span>
              <span className="mt-1.5 block text-[11.5px] text-[var(--color-ink-faint)]">
                {t.alunos} aluno{t.alunos === 1 ? "" : "s"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

SeletorTurma.propTypes = { onEscolher: PropTypes.func.isRequired };

export function ProfessorApp() {
  const { contexto: ctxEscopo } = useContextoIE("professor");
  const { contexto, setContexto } = usePerfil();
  const [periodo, setPeriodo] = useState(periodoInicial);

  const turmaId = contexto?.turmaId;

  if (!turmaId) {
    return (
      <PainelShell escopoLabel="Turma" escopoValor="selecione uma turma">
        <SeletorTurma onEscolher={setContexto} />
      </PainelShell>
    );
  }

  return (
    <PainelShell
      escopoLabel="Turma"
      escopoValor={[contexto.turmaNome, contexto.escolaNome]
        .filter(Boolean)
        .join(" · ")}
      toolbar={
        <div className="flex items-center">
          <PeriodoFiltro
            estado={periodo}
            ano={ctxEscopo?.anos?.[0]}
            onChange={setPeriodo}
          />
          <button
            type="button"
            className="ml-2 rounded-md px-2 py-1 text-[12px] font-medium text-[var(--color-brand)] underline-offset-2 transition-colors hover:bg-[var(--color-brand-dim)] hover:underline"
            onClick={() => setContexto(null)}
          >
            trocar turma
          </button>
        </div>
      }
    >
      <TurmaPainelView
        turmaId={turmaId}
        ano={ctxEscopo?.anos?.[0]}
        periodo={periodo}
      />
    </PainelShell>
  );
}
